/**
 * axios-interceptors.js — Iter 163.
 *
 * Registers a global response interceptor once at boot so any 402
 * from an entitlement-gated endpoint is converted into a typed
 * `EntitlementRequiredError` that carries the flag name + message.
 * Callers can then branch cleanly on `err.entitlementRequired` +
 * render a soft paywall instead of leaking the raw JSON body into JSX.
 *
 * This is the safety net Iter 163's QA pass asked for — any future
 * gated endpoint (patient, provider, admin, or otherwise) will now
 * degrade gracefully across the whole app.
 */
import axios from 'axios';

export class EntitlementRequiredError extends Error {
  constructor(detail, response) {
    const flag = detail?.entitlement_required || 'unknown';
    const alt = detail?.entitlement_required_any || [flag];
    const msg = detail?.message || `Entitlement '${flag}' required for this feature.`;
    super(msg);
    this.name = 'EntitlementRequiredError';
    this.entitlementRequired = flag;
    this.entitlementRequiredAny = alt;
    this.userMessage = msg;
    this.originalDetail = detail;
    this.status = 402;
    this.response = response;
  }
}

let _installed = false;

/** Idempotent installer — call once from index.js. */
export function installAxiosInterceptors() {
  if (_installed) return;
  _installed = true;
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 && detail && typeof detail === 'object'
          && detail.entitlement_required) {
        return Promise.reject(new EntitlementRequiredError(detail, err.response));
      }
      return Promise.reject(err);
    },
  );
}
